import RecordLink from '@components/Common/RecordLink';
import { actionLabel, actorPath, actorTypeLabel, entityPath } from '@constants/activityLabels';
import { getRecordActivityQuery } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { Box, Button } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import SectionCard, { WorkspaceField } from './SectionCard';
import { formatDateTime } from '@helpers/date';

interface Row {
  id: string;
  actorId: string | null;
  actorType: string | null;
  actorRole: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  timestamp: string | null;
}

const PAGE_SIZE = 25;

/**
 * The record's activity, for the History tab of every workspace: what was done to it and, for a
 * person (job seeker, employer, staff), what they did themselves. The same rows as the
 * Dashboard's Activity tab, read for one record, so a record's history and the platform's agree.
 */
const RecordActivityCard: React.FC<{ recordId?: string | null }> = ({ recordId }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback((p: number) => {
    if (!recordId) return;
    fetchGraphQLApiData(getRecordActivityQuery(recordId, p, PAGE_SIZE))
      .then((r) => {
        const data = r?.data?.getRecordActivity;
        setRows((prev) => (p === 0 ? data?.items ?? [] : [...prev, ...(data?.items ?? [])]));
        setTotal(data?.total ?? 0);
        setPage(p);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [recordId]);

  useEffect(() => load(0), [load]);

  const who = (r: Row) => {
    const name = r.actorName || r.actorEmail || (r.actorId === recordId ? 'This person' : 'Someone');
    const path = r.actorId && r.actorId !== recordId ? actorPath(r.actorType, r.actorId) : null;
    const label = `${name}, ${actorTypeLabel(r.actorType, r.actorRole)}`;
    return path ? <RecordLink to={path}>{label}</RecordLink> : <>{label}</>;
  };

  const what = (r: Row) => {
    const path = r.entityId && r.entityId !== recordId ? entityPath(r.entityType, r.entityId) : null;
    return (
      <>
        {actionLabel(r.action)}
        {path && (
          <>
            {' '}
            <RecordLink to={path}>{`Open ${r.entityType === 'JobSeeker' ? 'candidate' : r.entityType?.toLowerCase()}`}</RecordLink>
          </>
        )}
      </>
    );
  };

  return (
    <SectionCard
      title="Activity"
      icon={<TimelineOutlinedIcon />}
      meta={state === 'ready' ? `${total} recorded` : undefined}
    >
      {state === 'error' ? (
        <WorkspaceField label="Activity" value="Activity could not be loaded. Try again in a moment." full />
      ) : state === 'loading' ? (
        <WorkspaceField label="Activity" value="Loading" full />
      ) : rows.length === 0 ? (
        <WorkspaceField label="Activity" value="Nothing recorded yet" full />
      ) : (
        <Box className="cc-field--full cc-record-activity">
          {rows.map((r) => (
            <WorkspaceField
              key={r.id}
              label={formatDateTime(r.timestamp, '')}
              value={<>{what(r)} &middot; {who(r)}</>}
              full
            />
          ))}
          {rows.length < total && (
            <Button size="small" variant="outlined" onClick={() => load(page + 1)}>
              Show older activity
            </Button>
          )}
        </Box>
      )}
    </SectionCard>
  );
};

export default RecordActivityCard;
